import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { constants, NotificationManager, usePaymentMethods } from '@atlas/core';
import { Icon, Label } from '@atlas/ui';

import { CDN_TYPE_BASE, imageUrlCDN } from 'common/utils';
import useFormatter from 'hooks/useFormatter';

import PaymentInfo from './PaymentInfo';

const CRYPTO_ATOMIC_UNIT = 10 ** 9;

const DepositSuccessPopup = ({ data: { amount, currency, paymentMethodId }, id }) => {
  const { currencyFormatter } = useFormatter(currency);

  const { isLoading, paymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.DEPOSIT,
    constants.DEVICE_TYPES.DESKTOP,
  );

  const selectedPayment = paymentMethods.find(method => method.id === paymentMethodId);
  const logo = selectedPayment?.desktopImageLink;

  const imageStyle = useMemo(() => {
    if (!logo) return {};
    return { backgroundImage: `url(${imageUrlCDN(logo, CDN_TYPE_BASE)})` };
  }, [logo]);

  const formatCurrency = useMemo(() => {
    const isCrypto = constants.CRYPTO_CURRENCY_CODE.includes(currency);

    if (isCrypto) {
      return currencyFormatter.format(amount / CRYPTO_ATOMIC_UNIT, {
        style: 'decimal',
        valueIsInCents: false,
      });
    }

    return currencyFormatter.format(amount, { style: 'decimal' });
  }, [amount, currency, currencyFormatter]);

  const onCloseHandler = useCallback(() => NotificationManager.removeNotification(id), [id]);

  return (
    <div className="deposit-success-notification">
      <Icon className="deposit-success-notification__icon" name="success-bold" />
      <div className="deposit-success-notification__container">
        <Label
          className="deposit-success-notification__title"
          message="bonus_successfully_activated"
        />
        <div className="deposit-success-notification__subtitle-content">
          <p className="deposit-success-notification__subtitle">
            +{formatCurrency}{' '}
            <span className="deposit-success-notification__currency">{currency}</span>
          </p>
          <div className="deposit-success-notification__payment-info">
            <PaymentInfo
              imageStyle={imageStyle}
              isLoading={isLoading}
              logo={logo}
              paymentName={selectedPayment?.name}
            />
          </div>
        </div>
      </div>
      <Icon className="deposit-success-notification__close" name="close" onClick={onCloseHandler} />
      <div className="deposit-success-notification__timer" />
    </div>
  );
};

DepositSuccessPopup.propTypes = {
  data: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    paymentMethodId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DepositSuccessPopup;
